import { call, put } from 'redux-saga/effects';

import * as types from './score.types';
import * as actions from './score.actions';
import * as api from '../../api/request-config/score.api';

import { apiRequest } from '../../api/api.saga-services';
import { ISession } from '../session/session.types';

import * as sessionService from '../session/session.saga-service';
import * as userService from '../user/user.saga-service';
import * as meetingService from '../meeting/meeting.saga-service';
import * as kpiRowActions from '../kpi-row/kpi-row.actions';
import { userSelectors } from '../user/user.selectors';
import { IUser } from '../user/user.types';
import { meetingSelectors } from '../meeting/meeting.selectors';
import { IMeeting } from '../meeting/meeting.types';
import { select, selectWithParams } from '../../../utils/selector.utils';
import { scoreSelectors } from './score.selectors';
import { kpiRowSelectors } from '../kpi-row/kpi-row.selectors';
import { IKpiRow } from '../kpi-row/kpi-row.types';

export function* storeScore(score: types.IScore | types.IScore[]) {
  yield put(actions.storeScore(score));
}

export function* clearScore(scoreId: types.IScore['id']) {
  yield put(actions.clearScore(scoreId));
}
export function* clearAllScores() {
  const scoreIds: types.IScore['id'][] = yield select(scoreSelectors.selectResourceIds);
  yield put(actions.clearScore(scoreIds));
}
/* This method sends api request and handle response */
export function* getScoreBoard(searchParams: types.IScoreSearchParams) {
  yield call(userService.searchUsers);

  const { scores, sessions } = (yield call(apiRequest, api.getScoreBoardApi(searchParams))) as {
    scores: types.IScore[];
    sessions: ISession[];
  };

  yield call(sessionService.storeSession, sessions);
  yield call(storeScore, scores);

  const users = ((yield selectWithParams(
    userSelectors.selectResourcesById,
    scores.reduce(
      (prev, curr) => (prev.includes(curr.userId) ? [...prev] : [...prev, curr.userId]),
      [] as IUser['id'][],
    ),
  )) as IUser[]).filter((user) => !!user);

  const meetings = ((yield selectWithParams(
    meetingSelectors.selectResourcesById,
    sessions.reduce(
      (prev, curr) => (prev.includes(curr.meetingId) ? [...prev] : [...prev, curr.meetingId]),
      [] as IMeeting['id'][],
    ),
  )) as IMeeting[]).filter((meeting) => !!meeting);

  const usersToStore = users.map((user) => ({
    ...user,
    scores: scores.reduce(
      (prev, curr) => (curr.userId === user.id ? [...prev, curr.id] : [...prev]),
      [] as types.IScore['id'][],
    ),
  }));
  yield call(userService.storeUser, usersToStore);

  const meetingsToStore = meetings.map((meeting) => ({
    ...meeting,
    sessions: sessions.reduce(
      (prev, curr) => (curr.meetingId === meeting.id ? [...prev, curr.id] : [...prev]),
      [] as ISession['id'][],
    ),
  }));
  yield call(meetingService.storeMeeting, meetingsToStore);
}

/* This method sends api request and handle response */
export function* createScore(scoreInfo: types.IScoreCreate) {
  let score = (yield call(apiRequest, api.createScoreApi(scoreInfo))) as types.IScore;
  if (!score) {
    return;
  }
  score = { ...score, id: score.id + '' };
  yield call(storeScore, score);

  return score;
}

export function* updateScore(score: types.IScoreUpdate) {
  yield call(apiRequest, api.updateScoreApi(score));
  yield call(storeScore, score as types.IScore);
  return score;
}

export function* saveScoreOrder(scoreIds: types.ISaveScoreOrder) {
  yield call(apiRequest, api.saveScoreOrderApi(scoreIds));
  //yield call(storeScore, score as types.IScore);
  return scoreIds;
}

export function* updateScoresBulk(scoreIds: types.IScore['id'][], userId?: string) {
  const kpiRows = (yield selectWithParams(kpiRowSelectors.selectResourcesById, scoreIds)) as IKpiRow[];
  const response = (yield call(apiRequest, api.updateScoresBulkApi({ scoreIds, userId }))) as types.IScore[];
  if (!response) {
    return;
  }
  yield put(actions.storeScore(response));

  const kpiRowsToStore = kpiRows
    .filter((kpiRow) => kpiRow)
    .map((kpiRow) => {
      const score = response.find((s) => s.id === kpiRow.id);
      if (!score) {
        return null;
      }
      return { ...kpiRow, userId: score.userId };
    })
    .filter(Boolean) as IKpiRow[];
  yield put(kpiRowActions.storeKpiRow(kpiRowsToStore));

  return response;
}
